import { useEffect, useState } from 'react'
import { formatOrdinalDate } from '@/utils/deliveryDays'
import type { OneOffEligibleDate } from '@/utils/deliveryDays'
import '@/styles/components/confirm-modal.css'
import '@/styles/components/move-locker.css'

interface MoveLockerModalProps {
  isOpen: boolean
  productName: string
  /** The locker's own delivery day, formatted. Used in the intro when the move
   *  is interrupting a pause; empty for a plain move. */
  lockerDayLabel: string
  /**
   * The confirm step's second line describing the pause being carried out (e.g.
   * "Your delivery on Thursday 24th September is paused."). Empty for a plain
   * "move to another day" from the one-off's own Move button; its presence is
   * what marks this as a move-then-pause.
   */
  pauseNote: string
  /** Past the cut-off: it can no longer be moved (mirrors Remove). */
  pastCutoff: boolean
  options: OneOffEligibleDate[]
  selected: string
  onSelect: (date: string) => void
  onConfirm: () => void
  onClose: () => void
  isBusy: boolean
}

/**
 * Move a one-off (milk locker, DF-230) to another delivery day. Two steps: pick
 * the new day, then confirm. Reached either from the day's pause (move first,
 * then pause) or from the one-off's own Move button (move only). Past the
 * cut-off, or with no other delivery day to move it to, it can't be moved.
 */
function MoveLockerModal({
  isOpen,
  productName,
  lockerDayLabel,
  pauseNote,
  pastCutoff,
  options,
  selected,
  onSelect,
  onConfirm,
  onClose,
  isBusy,
}: MoveLockerModalProps): React.JSX.Element | null {
  const [step, setStep] = useState<'choose' | 'confirm'>('choose')
  const withPause = pauseNote !== ''

  // Always reopen on the first step, never mid-confirm from a previous run.
  useEffect(() => {
    if (isOpen) setStep('choose')
  }, [isOpen])

  if (!isOpen) return null

  const canMove = !pastCutoff && options.length > 0
  const selectedLabel = selected ? formatOrdinalDate(selected) : ''

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && !isBusy) onClose()
  }

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container confirm-modal" role="dialog" aria-modal="true">
        {!isBusy && (
          <button type="button" className="modal-close-floating" onClick={onClose} aria-label="Close">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {step === 'choose' ? (
          <>
            <div className="modal-content is-headless">
              <h2 className="move-locker-title">
                {withPause ? 'Your one off product is on that day' : 'Move your one off product'}
              </h2>
              <p className="confirm-message">
                {withPause
                  ? `You have a ${productName} booked for ${lockerDayLabel}. If it's paused it won't be delivered, so please move it to another day first.`
                  : `Pick another day for your ${productName} and we'll move it there.`}
              </p>

              {canMove && (
                <div className="move-locker-field">
                  <label className="move-locker-label" htmlFor="move-locker-date">
                    Move it to another day
                  </label>
                  <select
                    id="move-locker-date"
                    className="move-locker-select"
                    value={selected}
                    onChange={event => onSelect(event.target.value)}
                    disabled={isBusy}
                  >
                    {options.map(o => (
                      <option key={o.date} value={o.date}>
                        {formatOrdinalDate(o.date)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {pastCutoff && (
                <p className="move-locker-note">
                  It&apos;s too late to move your {productName}, it&apos;s already being prepared for
                  delivery{withPause ? ', so it can’t be paused' : ''}.
                </p>
              )}

              {!pastCutoff && options.length === 0 && (
                <p className="move-locker-note">
                  There&apos;s no other delivery day to move it to just now
                  {withPause ? ', so it can’t be paused' : ''}.
                </p>
              )}
            </div>

            <div className="modal-footer move-locker-footer">
              <button type="button" className="btn-cancel" onClick={onClose} disabled={isBusy}>
                Cancel
              </button>
              {canMove && (
                <button type="button" className="btn-confirm" onClick={() => setStep('confirm')}>
                  Next
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="modal-content is-headless">
              <h2 className="move-locker-title">Just to confirm</h2>
              <p className="confirm-message">Here&apos;s what will happen:</p>
              <ul className="move-locker-summary">
                <li>Your {productName} moves to {selectedLabel}.</li>
                {withPause && <li>{pauseNote}</li>}
              </ul>
            </div>

            <div className="modal-footer move-locker-footer">
              <button type="button" className="btn-cancel" onClick={() => setStep('choose')} disabled={isBusy}>
                Back
              </button>
              <button type="button" className="btn-confirm" onClick={onConfirm} disabled={isBusy}>
                {isBusy ? (<><span className="btn-spinner" />Saving…</>) : 'Confirm'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default MoveLockerModal
